import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  IconButton,
  TableSortLabel,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Snackbar,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  Search,
  ArrowUpward,
  ArrowDownward,
  Refresh,
  Delete,
} from "@mui/icons-material";

const GetgeekFeedbackTable = ({ sidebarOpen = true }) => {
  const API_BASE_URL = `${process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL}`;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    feedbackId: null,
    candidateName: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch data from API
  const fetchFeedbackData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/candidate-feedback/all/entries-direct`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setData(result.data);
        setPagination((prev) => ({
          ...prev,
          totalItems: result.data.length,
        }));
        setSnackbar({
          open: true,
          message: "Data loaded successfully",
          severity: "success",
        });
      } else {
        throw new Error(result.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("Error fetching feedback data:", err);
      setError(`Failed to load data: ${err.message}`);
      setSnackbar({
        open: true,
        message: `Failed to load data: ${err.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchFeedbackData();
  }, []);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter(
      (item) =>
        item.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.candidate_email
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.feedback_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.feedback_category
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.sentiment?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // Calculate pagination for filtered data
  const paginatedData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    const filtered = filteredData;

    const totalPages = Math.ceil(filtered.length / pagination.itemsPerPage);

    // Update pagination state
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      totalItems: filtered.length,
    }));

    return filtered.slice(startIndex, endIndex);
  }, [filteredData, pagination.currentPage, pagination.itemsPerPage]);

  // Delete feedback (you can implement this API call)
  const deleteFeedback = async (feedbackId) => {
    try {
      setDeleteLoading((prev) => ({ ...prev, [feedbackId]: true }));

      // Simulate API call - replace with actual delete endpoint
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Remove from local state
      setData((prevData) =>
        prevData.filter((item) => item.feedback_id !== feedbackId)
      );

      setSnackbar({
        open: true,
        message: "Feedback deleted successfully",
        severity: "success",
      });

      // If current page becomes empty, go to previous page
      if (paginatedData.length === 1 && pagination.currentPage > 1) {
        setPagination((prev) => ({
          ...prev,
          currentPage: prev.currentPage - 1,
        }));
      }
    } catch (err) {
      console.error("Error deleting feedback:", err);
      setSnackbar({
        open: true,
        message: `Delete failed: ${err.message}`,
        severity: "error",
      });
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [feedbackId]: false }));
      setDeleteDialog({ open: false, feedbackId: null, candidateName: "" });
    }
  };

  // Handle delete click
  const handleDeleteClick = (feedbackId, candidateName) => {
    setDeleteDialog({
      open: true,
      feedbackId: feedbackId,
      candidateName: candidateName,
    });
  };

  // Handle delete confirm
  const handleDeleteConfirm = () => {
    if (deleteDialog.feedbackId) {
      deleteFeedback(deleteDialog.feedbackId);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, feedbackId: null, candidateName: "" });
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  // Handle refresh
  const handleRefresh = async () => {
    await fetchFeedbackData();
  };

  // Sort data locally
  const sortedData = useMemo(() => {
    return [...paginatedData].sort((a, b) => {
      const dateA = new Date(a.created_date || a.updated_date);
      const dateB = new Date(b.created_date || b.updated_date);

      if (sortOrder === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  }, [paginatedData, sortOrder]);

  const handleSortChange = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get sentiment color
  const getSentimentColor = (sentiment) => {
    const colors = {
      "Very Good": "success",
      Good: "primary",
      Neutral: "default",
      Bad: "warning",
      "Very Bad": "error",
    };
    return colors[sentiment] || "default";
  };

  // Get feedback category color
  const getFeedbackCategoryColor = (category) => {
    const colors = {
      "Test panel": "primary",
      Interview: "secondary",
      General: "default",
    };
    return colors[category] || "default";
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarOpen ? 220 : 100;

  // Skeleton Row Component
  const SkeletonRow = () => (
    <TableRow
      sx={{
        "&:nth-of-type(even)": {
          bgcolor: "#f5f5f5",
        },
      }}
    >
      <TableCell sx={{ minWidth: 150 }}>
        <Skeleton variant="text" width="80%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        <Skeleton variant="text" width="90%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Skeleton variant="rounded" width={80} height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 300 }}>
        <Skeleton variant="text" width="95%" height={20} />
        <Skeleton variant="text" width="85%" height={20} />
        <Skeleton variant="text" width="70%" height={20} />
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        <Skeleton variant="rounded" width={100} height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Skeleton variant="text" width="70%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 80, textAlign: "center" }}>
        <Skeleton variant="circular" width={32} height={32} />
      </TableCell>
    </TableRow>
  );

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: "#fafafa",
        minHeight: "100vh",
        ml: {
          xs: "0px",
          sm: "0px",
          md: `${sidebarWidth}px`,
          xl: `${sidebarWidth}px`,
        },
        transition: "margin-left 0.3s ease",
        fontFamily: "Outfit",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "#9048FB",
            fontWeight: 600,
            fontFamily: "Outfit",
          }}
        >
          Feedbacks Provided By Candidate For Getgeek Platform on interview
          Pannel
        </Typography>
        <IconButton
          onClick={handleRefresh}
          disabled={loading}
          sx={{
            bgcolor: "#9048FB",
            color: "white",
            "&:hover": { bgcolor: "#7c3aed" },
          }}
        >
          <Refresh />
        </IconButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Sort Controls */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by name, email, feedback, or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            maxWidth: 400,
            backgroundColor: "white",
            "& .MuiInputBase-input": {
              fontFamily: "Outfit",
              fontSize: "16px",
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Outfit" }}
          >
            Sort:
          </Typography>
          <IconButton
            onClick={handleSortChange}
            sx={{
              bgcolor: "#9048FB",
              color: "white",
              "&:hover": { bgcolor: "#7c3aed" },
            }}
          >
            {sortOrder === "desc" ? <ArrowDownward /> : <ArrowUpward />}
          </IconButton>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Outfit" }}
          >
            {sortOrder === "desc" ? "Newest" : "Oldest"}
          </Typography>
        </Box>
      </Box>

      {/* Data Table */}
      <Paper sx={{ overflow: "hidden", boxShadow: 2 }}>
        <TableContainer
          sx={{
            position: "relative",
            maxHeight: 700,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a1a1a1",
            },
          }}
        >
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: "#9048FB" }}>
              <TableRow>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                  }}
                >
                  Sentiment
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 300,
                    whiteSpace: "nowrap",
                  }}
                >
                  Feedback Text
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  Category
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                  }}
                >
                  <TableSortLabel
                    active={true}
                    direction={sortOrder}
                    onClick={handleSortChange}
                    sx={{
                      color: "white !important",
                      fontFamily: "Outfit",
                      "& .MuiTableSortLabel-icon": {
                        color: "white !important",
                      },
                    }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 80,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Show skeleton rows when loading
                Array.from({ length: 8 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "text.secondary",
                      fontFamily: "Outfit",
                      fontSize: "16px",
                    }}
                  >
                    {searchTerm ? "No results found" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row.feedback_id}
                    sx={{
                      "&:nth-of-type(even)": {
                        bgcolor: "#f5f5f5",
                      },
                      "&:hover": {
                        bgcolor: "#f3f0ff",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontFamily: "Outfit",
                        fontSize: "16px",
                        minWidth: 150,
                      }}
                    >
                      {row.candidate_name || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#9048FB",
                        fontFamily: "Outfit",
                        fontSize: "16px",
                        minWidth: 200,
                      }}
                    >
                      {row.candidate_email || "N/A"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Chip
                        label={row.sentiment || "N/A"}
                        size="small"
                        color={getSentimentColor(row.sentiment)}
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "14px",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 300, maxWidth: 300 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.4,
                        }}
                      >
                        {row.feedback_text || "No feedback provided"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <Chip
                        label={row.feedback_category || "N/A"}
                        size="small"
                        color={getFeedbackCategoryColor(row.feedback_category)}
                        variant="outlined"
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "14px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#A3AED0",
                        fontFamily: "Outfit",
                        fontSize: "16px",
                        minWidth: 120,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(row.created_date || row.updated_date)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 80, textAlign: "center" }}>
                      <Tooltip title="Delete feedback">
                        <IconButton
                          onClick={() =>
                            handleDeleteClick(
                              row.feedback_id,
                              row.candidate_name
                            )
                          }
                          disabled={deleteLoading[row.feedback_id]}
                          sx={{
                            color: "#f44336",
                            "&:hover": {
                              backgroundColor: "rgba(244, 67, 54, 0.1)",
                            },
                            "&:disabled": {
                              color: "rgba(244, 67, 54, 0.3)",
                            },
                          }}
                        >
                          {deleteLoading[row.feedback_id] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Delete />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination and Results Summary */}
      <Box
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: "Outfit", fontSize: "16px" }}
        >
          {loading ? (
            <Skeleton variant="text" width={200} height={24} />
          ) : (
            <>
              Showing {sortedData.length} of {pagination.totalItems} entries
              {searchTerm && ` (filtered by "${searchTerm}")`}
            </>
          )}
        </Typography>

        {pagination.totalPages > 1 && !loading && (
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#9048FB",
                fontFamily: "Outfit",
                fontSize: "16px",
                "&.Mui-selected": {
                  backgroundColor: "#9048FB",
                  color: "white",
                },
                "&:hover": {
                  backgroundColor: "#f3f0ff",
                },
              },
            }}
            disabled={loading}
          />
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 600,
            color: "#f44336",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="delete-dialog-description"
            sx={{
              fontFamily: "Outfit",
              fontSize: "16px",
            }}
          >
            Are you sure you want to delete the feedback from{" "}
            <strong>{deleteDialog.candidateName}</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              fontFamily: "Outfit",
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              fontFamily: "Outfit",
              backgroundColor: "#f44336",
              "&:hover": {
                backgroundColor: "#d32f2f",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontFamily: "Outfit",
            fontSize: "16px",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GetgeekFeedbackTable;
